/**
 * KartotekaViewFormBuilder — Etap 3 T3.1: builder wizualny formularza dla NAZWANEGO
 * widoku biblioteki kartoteki (`kartoteka_views`, viewType='form').
 *
 * Osadza BOGATY builder `SystemModuleBuilderPage` (embedded) — ten sam, którego używa
 * tenant: panel stylów, wyróżnik jako blok view-level, zakładki/sekcje/kontenery. Zapis
 * widoku (`ModuleTemplateFormConfig`, pola SLUGIEM) ⇄ drzewo buildera (`fieldId`) tłumaczy
 * dwukierunkowy konwerter `templateFormConfigToBuilderTree` / `builderTreeToTemplateFormConfig`
 * z `@saas/shared`. Panel ITE nie zna bytów tenanta → mapa identycznościowa `id ≡ slug`;
 * materializacja u tenanta (`translateKartotekaViewConfigToLayoutConfig`) tłumaczy slug→fieldId
 * i zachowuje wyróżnik + `settings.style` (rekurencyjnie, bez spłaszczania).
 *
 * Świeży widok (pusty config) → seed: jedna zakładka „Dane" z sekcją WSZYSTKICH pól
 * kartoteki wg `sortOrder` (projektant nie zaczyna od pustego płótna).
 *
 * Osadzony w zakładce „Layouty formularzy" (`KartotekaFormViewsPanel`) — zastępuje listę
 * na czas edycji. Zapis idzie przyciskiem „Zapisz" WEWNĄTRZ płótna (jak w hubie);
 * „Wróć do listy" tylko przełącza widok (builder sam pilnuje `dirty`).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { KartotekaViewDto, ModuleTemplateFormConfig } from '@saas/shared';
import type { BuilderFormTreeConfig } from '@saas/shared';
import {
  builderTreeToTemplateFormConfig,
  identityFieldMap,
  templateFormConfigToBuilderTree,
} from '@saas/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { useToast } from '@/contexts/ToastContext';
import type {
  KartotekaBuilderField,
  KartotekaIdentityRef,
} from '@/lib/kartoteki/kartoteka-definition';
import {
  kartotekaLibraryViewsQueryKey,
  updateKartotekaLibraryView,
  useKartotekaDefinition,
} from '@/lib/kartoteki/kartoteka-definition';
import { SystemModuleBuilderPage } from '@/pages/system-modules/SystemModuleBuilderPage';

/**
 * Zakładki-placeholdery (audyt, drzewo, satelita itd.) są READ-ONLY co do
 * TREŚCI — biblioteka widoków służy do układania pól GŁÓWNEJ encji + wyróżnika + stylów.
 * Podkolekcje (Adresy, Konta bankowe…) świadomie NIE są tu — tak jak w edytorze szablonu
 * (`ReferenceFormBuilderEmbedded`) i u tenanta MOŻNA je usuwać/przywracać/przestawiać;
 * ich pola pomocnicze podajemy z definicji (externalHelperFields), więc render nie strzela
 * zapytań tenant-scope (401 w sesji admina). Lustro `KARTOTEKA_READ_ONLY_TAB_TYPES`.
 */
const VIEW_BUILDER_READ_ONLY_TAB_TYPES: Array<
  'tree_picker' | 'satellite' | 'additional' | 'panel' | 'audit_log' | 'approvals'
> = ['tree_picker', 'satellite', 'additional', 'panel', 'audit_log', 'approvals'];

/**
 * Kolekcje podkolekcji zablokowane co do treści (dostępowe, nie edytowalne w widoku) —
 * lustro `KARTOTEKA_READ_ONLY_COLLECTIONS` z `ModuleTemplateEditor`.
 */
const VIEW_BUILDER_READ_ONLY_COLLECTIONS: string[] = [
  'contractor_company_access',
  'user_company_access',
];

interface KartotekaViewFormBuilderProps {
  identity: KartotekaIdentityRef;
  fields: KartotekaBuilderField[];
  view: KartotekaViewDto;
  onBack: () => void;
  /**
   * Zamiast zapisu do biblioteki — oddaj gotowy config wołającemu (kreator menu modułu
   * zapisuje układ obcej kartoteki razem z menu, decyzja 2026-10-05).
   */
  onSaveConfig?: (config: ModuleTemplateFormConfig) => void;
}

/**
 * Domyślne drzewo buildera dla świeżego widoku (brak zapisanego configu): jedna zakładka
 * „Dane" z sekcją zawierającą WSZYSTKIE pola kartoteki wg `sortOrder`. Pola adresowane
 * `fieldId ≡ slug` (mapa identycznościowa panelu ITE).
 */
function buildDefaultBuilderTree(
  fields: KartotekaBuilderField[],
  defaultTabTitle: string,
): Record<string, unknown> {
  const sorted = [...fields].sort((a, b) => a.sortOrder - b.sortOrder);
  return {
    elements: [
      {
        id: 'kartoteka-view-tabs-0',
        type: 'tabs',
        tabs: [
          {
            id: 'kartoteka-view-tab-data',
            slug: 'data',
            title: defaultTabTitle,
            hidden: false,
            sortOrder: 0,
            children: [
              {
                id: 'section-default',
                type: 'section',
                settings: { columns: 1, showHeader: false, showBorder: false, collapsible: false },
                children: sorted.map((f) => ({
                  id: `el-${f.slug}`,
                  type: 'field',
                  fieldId: f.slug,
                  settings: { label: f.name, required: f.isRequired, width: 1 },
                })),
              },
            ],
          },
        ],
      },
    ],
  };
}

function isEmptyFormConfig(config: unknown): boolean {
  if (!config || typeof config !== 'object') return true;
  const elements = (config as { elements?: unknown }).elements;
  return !Array.isArray(elements) || elements.length === 0;
}

export function KartotekaViewFormBuilder({
  identity,
  fields,
  view,
  onBack,
  onSaveConfig,
}: KartotekaViewFormBuilderProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const fieldSlugs = useMemo(() => fields.map((f) => f.slug), [fields]);
  // Mapa identycznościowa slug↔slug (id ≡ slug w panelu ITE) — w obie strony ta sama.
  const fieldMap = useMemo(() => identityFieldMap(fieldSlugs), [fieldSlugs]);

  // Pola/layouty pomocnicze podkolekcji (Adresy, Konta bankowe…) z definicji kartoteki —
  // dzięki nim edytor podkolekcji renderuje się bez zapytań tenant-scope (401 w scope admina),
  // a same zakładki podkolekcji można usuwać/przywracać/przestawiać (parytet z tenantem).
  // Tylko dla kartoteki systemowej (identity.key = slug); dla encji Kreatora hook wyłączony.
  const kartotekaDef = useKartotekaDefinition(
    identity.kind === 'system' ? identity.key : undefined,
  );
  const externalHelperFields = kartotekaDef.data?.helperFields;
  const externalHelperLayouts = kartotekaDef.data?.helperDefaultLayouts;

  const defaultTabTitle = t('moduleTemplateEditor.referencePanel.formViewBuilder.defaultTabTitle', {
    defaultValue: 'Dane',
  });

  // Pola palety bogatego buildera (kształt SystemModuleField): `id ≡ slug`, typ pola SUROWY
  // (builder systemowy operuje na typach systemowych, nie na palecie starego FieldRegistry).
  const externalFields = useMemo(
    () =>
      [...fields]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((f) => ({
          id: f.slug,
          slug: f.slug,
          name: f.name,
          fieldType: f.fieldType,
          isCore: f.isCore,
          isSystemLocked: f.isSystemLocked,
          isRequired: f.isRequired,
          sortOrder: f.sortOrder,
          description: null,
        })),
    [fields],
  );

  const initialLayoutConfig = useMemo<Record<string, unknown>>(() => {
    if (isEmptyFormConfig(view.config)) {
      return buildDefaultBuilderTree(fields, defaultTabTitle);
    }
    return templateFormConfigToBuilderTree(
      view.config as ModuleTemplateFormConfig,
      fieldMap,
      defaultTabTitle,
    ) as unknown as Record<string, unknown>;
    // `view.slug` w zależnościach: przełączenie edytowanego widoku odświeża initial config.
  }, [view.config, view.slug, fields, fieldMap, defaultTabTitle]);

  const handleSave = async (layoutConfig: Record<string, unknown>) => {
    const config = builderTreeToTemplateFormConfig(
      layoutConfig as unknown as BuilderFormTreeConfig,
      fieldMap,
    );
    if (onSaveConfig) {
      onSaveConfig(config);
      return;
    }
    await updateKartotekaLibraryView(identity, view.slug, {
      name: view.name,
      viewType: 'form',
      config: config as unknown as Record<string, unknown>,
      sortOrder: view.sortOrder,
    });
    await queryClient.invalidateQueries({ queryKey: kartotekaLibraryViewsQueryKey(identity) });
    toast.show({
      variant: 'success',
      description: t('moduleTemplateEditor.referenceFormBuilder.savedToast', {
        defaultValue: 'Layout kartoteki zapisany.',
      }),
    });
  };

  return (
    <div data-testid="kartoteka-view-form-builder">
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 rounded p-1.5 text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          data-testid="kartoteka-view-builder-back"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('moduleTemplateEditor.referencePanel.formViewBuilder.back')}
        </button>
        <span className="text-sm font-medium text-text-primary">{view.name}</span>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <SystemModuleBuilderPage
          key={view.slug}
          embeddedMode
          slug={identity.key}
          initialLayoutConfig={initialLayoutConfig}
          // Świeży widok startuje z wygenerowanej zakładki „Dane" — to do zapisania, nie stan bazy.
          initialDirty={isEmptyFormConfig(view.config)}
          onSaveKartotekaLayout={handleSave}
          // „Dodaj pole" w scope admina idzie ścieżką kartoteki (POST /admin/kartoteki/:slug/fields,
          // add-only) — tylko dla kartoteki systemowej (identity.key = slug).
          addFieldKartotekaSlug={identity.kind === 'system' ? identity.key : undefined}
          allowTabManagement
          readOnlyTabTypes={VIEW_BUILDER_READ_ONLY_TAB_TYPES}
          readOnlyTabCollections={VIEW_BUILDER_READ_ONLY_COLLECTIONS}
          externalFields={externalFields}
          externalHelperFields={externalHelperFields}
          externalHelperLayouts={externalHelperLayouts}
        />
      </div>
    </div>
  );
}
